import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Check, ChevronDown, ChevronRight, Target, AlertTriangle, Sparkles } from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import StrategyBriefModal from './StrategyBriefModal';
import logger from '../../lib/logger';

function formatCurrencyShort(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const FIT_STYLE = {
  strong:   { color: 'var(--color-success)',     label: 'Strong fit'   },
  moderate: { color: 'var(--color-warning)',     label: 'Moderate fit' },
  weak:     { color: 'var(--color-danger)',      label: 'Weak / not a real competitor' },
  unknown:  { color: 'var(--color-text-muted)',  label: 'Unmapped'     },
};

function DisplacementTable({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown }) {
  const queryClient = useQueryClient();
  const [addedIds, setAddedIds] = useState({});
  const [expandedKeys, setExpandedKeys] = useState({});
  const [fitFilter, setFitFilter] = useState('all'); // all | strong | moderate
  // Freshness gate. Default ON so contracts that ended >18 months ago
  // (likely already renewed via a different PO that we can't track here)
  // are excluded. Toggle off to audit the full historical picture.
  const [includeStale, setIncludeStale] = useState(false);
  // Opportunity currently being explained by Claude. null = closed.
  const [briefOpportunity, setBriefOpportunity] = useState(null);

  const qs = new URLSearchParams();
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  if (includeStale) qs.set('includeStale', 'true');
  if (productFamily) qs.set('product_family', productFamily);
  // Phase H.29: department_ids filter for list scoping
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  const suffix = qs.toString() ? `?${qs}` : '';
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/displacement${suffix}`;
  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'displacement', vendor, includeLowConfidence, strict, includeStale, productFamily, departmentIds, years],
    queryFn: loggedGet('DisplacementTable:fetch', path),
    enabled: !!vendor,
  });

  const addCardMutation = useMutation({
    mutationFn: loggedMutation(
      'DisplacementTable:add-to-plan',
      (body) => apiClient.post(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards`,
        body,
      ),
    ),
    onSuccess: () => {
      logger.vendorAnalysis.info('DisplacementTable:plan-invalidated', { vendor });
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
    },
  });

  const handleAdd = (target) => {
    const key = rowKey(target);
    const counter = target.recommended_counter_product
      ? `Counter with ${target.recommended_counter_product}.`
      : '';
    addCardMutation.mutate({
      column_key: 'backlog',
      title: `Displace ${target.competitor}${target.competitor_product ? ` (${target.competitor_product})` : ''} at ${target.agency}`,
      rationale: [
        `${target.agency} spent ${formatCurrencyShort(target.competitor_spend)} with ${target.competitor}${target.competitor_product ? ` on ${target.competitor_product}` : ''} (${target.po_line_count} line${target.po_line_count === 1 ? '' : 's'}).`,
        target.latest_end_date ? `Latest contract end: ${formatDate(target.latest_end_date)}.` : '',
        target.vendor_already_present
          ? `${vendor} already has presence in this category — focus on expansion.`
          : `${vendor} has no recorded presence — net-new displacement target.`,
        counter,
        target.positioning_strategy,
      ].filter(Boolean).join(' '),
      dollar_impact: target.competitor_spend,
      due_date: target.latest_end_date,
      seed_kind: 'displacement',
      seed_payload: {
        department_id: target.department_id,
        agency: target.agency,
        category: target.category,
        competitor: target.competitor,
        competitor_product: target.competitor_product,
        vendor_already_present: target.vendor_already_present,
        recommended_counter_product: target.recommended_counter_product,
        positioning_fit: target.positioning_fit,
      },
    }, {
      onSuccess: () => setAddedIds(prev => ({ ...prev, [key]: true })),
    });
  };

  const targets = data?.targets || [];
  const filteredTargets = useMemo(() => {
    if (fitFilter === 'all') return targets;
    return targets.filter(t => t.positioning_fit === fitFilter);
  }, [targets, fitFilter]);

  const fitCounts = useMemo(() => ({
    strong: targets.filter(t => t.positioning_fit === 'strong').length,
    moderate: targets.filter(t => t.positioning_fit === 'moderate').length,
    weak: targets.filter(t => t.positioning_fit === 'weak').length,
    unknown: targets.filter(t => t.positioning_fit === 'unknown' || !t.positioning_fit).length,
  }), [targets]);

  if (isLoading) return <div className="vca-section vca-section__loading">Loading displacement targets…</div>;
  if (error) return <div className="vca-section vca-section__error">Displacement failed: {error.message}</div>;
  if (targets.length === 0) {
    return (
      <div className="vca-section">
        <h2>Displacement targets</h2>
        <p className="vca-section__sub">No competitor spend found in {vendor}'s shared categories.</p>
      </div>
    );
  }

  const staleCount = targets.filter(t => t.freshness === 'stale').length;
  const activeCount = targets.filter(t => !t.freshness || t.freshness === 'active').length;

  return (
    <div className="vca-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2>Displacement targets</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <FitFilterChip current={fitFilter} value="all" onClick={setFitFilter}>
            All ({targets.length})
          </FitFilterChip>
          <FitFilterChip current={fitFilter} value="strong" onClick={setFitFilter}>
            <span style={{ color: FIT_STYLE.strong.color }}>●</span> Strong ({fitCounts.strong})
          </FitFilterChip>
          <FitFilterChip current={fitFilter} value="moderate" onClick={setFitFilter}>
            <span style={{ color: FIT_STYLE.moderate.color }}>●</span> Moderate ({fitCounts.moderate})
          </FitFilterChip>
          {fitCounts.weak > 0 && (
            <FitFilterChip current={fitFilter} value="weak" onClick={setFitFilter}>
              <span style={{ color: FIT_STYLE.weak.color }}>●</span> Weak ({fitCounts.weak})
            </FitFilterChip>
          )}
          <label
            className="vca-toggle"
            style={{ marginLeft: 12, fontSize: '0.78rem' }}
            title="Contracts whose latest end-date is more than 18 months ago. These have almost certainly been renewed via a different PO already — including them in 'displacement' is misleading."
          >
            <input
              type="checkbox"
              checked={includeStale}
              onChange={(e) => setIncludeStale(e.target.checked)}
            />
            <span>Include expired (&gt;18mo old)</span>
          </label>
        </div>
      </div>
      <p className="vca-section__sub">
        Each row is one (agency, competitor, product) tuple. Click a row to expand the sample line description and the {vendor} positioning strategy.
        {' '}{activeCount} active{staleCount > 0 ? ` · ${staleCount} stale (ended in past 18 months — possibly already renewed elsewhere)` : ''}.
        {' '}Use the fit filter to focus on strong head-to-head plays.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="vca-table">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Agency</th>
              <th>Competitor product</th>
              <th>Recommended counter</th>
              <th className="vca-num">Spend</th>
              <th className="vca-num">PO lines</th>
              <th>Latest end</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredTargets.map(t => {
              const key = rowKey(t);
              const added = !!addedIds[key];
              const expanded = !!expandedKeys[key];
              const fit = FIT_STYLE[t.positioning_fit] || FIT_STYLE.unknown;
              return (
                <React.Fragment key={key}>
                  <tr
                    className="clickable"
                    onClick={() => setExpandedKeys(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <td>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td>{t.agency}</td>
                    <td>
                      <div><strong>{t.competitor}</strong>{t.competitor_product ? ` · ${t.competitor_product}` : ''}</div>
                    </td>
                    <td>
                      {t.recommended_counter_product ? (
                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Target size={12} style={{ color: fit.color }} />
                            {t.recommended_counter_product}
                          </span>
                          <div className="vca-section__sub" style={{ fontSize: '0.72rem', color: fit.color }}>
                            {fit.label}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: fit.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <AlertTriangle size={12} />
                          {fit.label}
                        </span>
                      )}
                    </td>
                    <td className="vca-num">{formatCurrencyShort(t.competitor_spend)}</td>
                    <td className="vca-num">{t.po_line_count}</td>
                    <td>
                      {formatDate(t.latest_end_date)}
                      {t.freshness === 'stale' && (
                        <div className="vca-section__sub" style={{ fontSize: '0.7rem', color: 'var(--color-warning)' }}>
                          stale · likely already renewed
                        </div>
                      )}
                      {t.freshness === 'expired' && (
                        <div className="vca-section__sub" style={{ fontSize: '0.7rem', color: 'var(--color-danger)' }}>
                          expired &gt;18mo
                        </div>
                      )}
                    </td>
                    <td>{t.vendor_already_present ? 'Vendor present' : 'Net-new'}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                          className="vca-add-btn"
                          style={{
                            background: 'transparent',
                            color: 'var(--color-accent)',
                            border: '1px solid color-mix(in srgb, var(--color-accent) 35%, var(--color-border))',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            logger.vendorAnalysis.info('DisplacementTable:why-click', {
                              vendor, competitor: t.competitor, agency: t.agency,
                              competitor_product: t.competitor_product,
                            });
                            // Map displacement-row shape to the modal's opportunity shape.
                            // Displacement returns competitor_spend (not contract_value),
                            // latest_end_date (not end_date), and vendor_already_present
                            // (not play_type). Translate so the modal — and the underlying
                            // /strategy-brief endpoint — get a consistent payload.
                            setBriefOpportunity({
                              ...t,
                              contract_value: t.competitor_spend,
                              end_date: t.latest_end_date,
                              line_count: t.po_line_count,
                              top_component: t.competitor_product,
                              play_type: t.vendor_already_present ? 'coexist' : 'offensive',
                            });
                          }}
                          title="Generate AI strategy brief for this opportunity"
                        >
                          <Sparkles size={12} /> Why?
                        </button>
                        <button
                          className="vca-add-btn"
                          disabled={added || addCardMutation.isPending || t.positioning_fit === 'weak'}
                          title={t.positioning_fit === 'weak' ? 'Weak fit — likely not a real competitor for this vendor' : undefined}
                          onClick={(e) => { e.stopPropagation(); handleAdd(t); }}
                        >
                          {added ? <Check size={14} /> : <Plus size={14} />} {added ? 'Added' : 'Add to plan'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="vca-row-detail">
                      <td colSpan={9}>
                        <div className="vca-detail-grid">
                          <div>
                            <div className="vca-detail-label">Sample line description</div>
                            <div className="vca-detail-text">
                              {t.sample_description || <em>No description recorded.</em>}
                            </div>
                          </div>
                          <div>
                            <div className="vca-detail-label">Positioning strategy</div>
                            <div className="vca-detail-text">
                              {t.positioning_strategy || <em>No strategy mapped.</em>}
                            </div>
                          </div>
                          <div>
                            <div className="vca-detail-label">Quick actions</div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                className="vca-add-btn"
                                style={{ background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDrilldown({
                                    type: 'vendor',
                                    value: t.competitor,
                                    department: t.agency,
                                    departmentId: t.department_id,
                                  });
                                }}
                              >
                                See {t.competitor} POs at {t.agency}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <StrategyBriefModal
        vendor={vendor}
        opportunity={briefOpportunity}
        isOpen={!!briefOpportunity}
        onClose={() => setBriefOpportunity(null)}
        onAddToPlan={(enrichedRow) => {
          // Modal hands back the opportunity shape + ai_brief +
          // rationale_override. Seed a kanban card with the AI rationale
          // verbatim so the reasoning persists with the play.
          addCardMutation.mutate({
            column_key: 'backlog',
            title: `${enrichedRow.play_type === 'offensive' ? 'Displace' : 'Wrap'} ${enrichedRow.competitor}${enrichedRow.competitor_product ? ` (${enrichedRow.competitor_product})` : ''} at ${enrichedRow.agency}`,
            rationale: enrichedRow.rationale_override,
            dollar_impact: enrichedRow.competitor_spend ?? enrichedRow.contract_value,
            due_date: enrichedRow.latest_end_date ?? enrichedRow.end_date,
            seed_kind: enrichedRow.play_type === 'offensive' ? 'displacement_offensive_ai' : 'displacement_coexist_ai',
            seed_payload: {
              department_id: enrichedRow.department_id,
              agency: enrichedRow.agency,
              category: enrichedRow.category,
              competitor: enrichedRow.competitor,
              competitor_product: enrichedRow.competitor_product,
              vendor_already_present: enrichedRow.vendor_already_present,
              recommended_counter_product: enrichedRow.recommended_counter_product,
              positioning_fit: enrichedRow.positioning_fit,
              ai_brief: enrichedRow.ai_brief,
            },
          }, {
            onSuccess: () => setAddedIds(prev => ({
              ...prev,
              [rowKey(enrichedRow)]: true,
            })),
          });
        }}
      />
    </div>
  );
}

function FitFilterChip({ current, value, onClick, children }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      style={{
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-full)',
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: '0.78rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {children}
    </button>
  );
}

function rowKey(t) {
  return `${t.department_id}-${t.competitor}-${t.competitor_product || t.category}`;
}

export default DisplacementTable;
